import { queries } from 'RestAPIs/rapini';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import WithInnerLayout from 'components/shared/CRUD/components/InnerPage/WithInnerLayout';
import ProtocolDetail from './components/ProtocolDetail';
import ProtocolDrawer from './components/ProtocolDrawer';
import ProtocolsFilters from './components/ProtocolsFilters/ProtocolsFilters';
import { useProtocolColumns } from './hooks/useProtocolColumns';
import { useProtocolDrawer } from './hooks/useProtocolDrawer';
import { useProtocolFilters } from './hooks/useProtocolFilters';
import { useSelectedProtocol } from './hooks/useSelectedProtocol';
import { filterProtocols } from './Protocols.utils';

import type { WithInnerLayoutProps } from 'components/shared/CRUD/components/InnerPage/WithInnerLayout';
import type { ProtocolResponse } from 'generated/index';

const Protocols = (innerLayout: WithInnerLayoutProps) => {
  'use memo';
  const { control, validityOptions, search, filter } = useProtocolFilters();
  const {
    data: protocols = [],
    isLoading,
    isError,
    refetch
  } = queries.useGetProtocols();
  const columns = useProtocolColumns();
  const { selectedProtocol, openProtocol, closeProtocol } = useSelectedProtocol(
    protocols,
    innerLayout
  );
  const drawer = useProtocolDrawer();

  const visibleProtocols = filterProtocols(protocols, search, filter);

  // A new protocol opens its detail view straight away; an edited one is already showing it
  const handleSaved = ({ id }: ProtocolResponse) => {
    drawer.close();
    openProtocol(id);
  };

  return (
    <>
      {selectedProtocol ? (
        <ProtocolDetail
          protocol={selectedProtocol}
          protocols={protocols}
          onSelect={openProtocol}
          onBack={closeProtocol}
          onEdit={() => drawer.openEdit(selectedProtocol)}
        />
      ) : (
        <>
          <ProtocolsFilters
            control={control}
            validityOptions={validityOptions}
          />
          <TableCRUDLayout
            loading={isLoading}
            columns={columns}
            rows={visibleProtocols}
            getRowId={(protocol) => protocol.id}
            hasLoadingError={isError}
            onLoadingError={refetch}
            renderFormContainer={false}
            onAddClick={drawer.openCreate}
            editable={false}
            onViewClick={(protocolId) => openProtocol(String(protocolId))}
            onRowClick={({ id }) => openProtocol(String(id))}
            translationKey={
              protocols.length === 0
                ? 'adminPortal.protocols.noProtocols'
                : 'adminPortal.protocols.noSearchResults'
            }
          />
        </>
      )}
      <ProtocolDrawer
        open={drawer.isOpen}
        protocol={drawer.protocol}
        onClose={drawer.close}
        onSaved={handleSaved}
      />
    </>
  );
};

const ProtocolsWithLayout = WithInnerLayout(Protocols);
export default ProtocolsWithLayout;
